import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './BFDrawer.css';

export interface BFDrawerProps {
    /** Whether the drawer is visible */
    open: boolean;
    /** Title displayed in the drawer header. Omit to show only the close button. */
    title?: string;
    /** Callback fired when the drawer should close */
    onClose: () => void;
    /** Drawer body content */
    children: React.ReactNode;
    /** Drawer width (CSS value, e.g. '35%', '480px') */
    width?: string | number;
    /** Whether clicking the overlay calls onClose */
    closeOnOverlay?: boolean;
}

const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function BFDrawer({ open, title, onClose, children, width = '35%', closeOnOverlay = true }: BFDrawerProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useRef(`bf-drawer-title-${Math.random().toString(36).slice(2)}`).current;
    const previousFocusRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (open) {
            previousFocusRef.current = document.activeElement as HTMLElement;
            requestAnimationFrame(() => {
                const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTORS);
                first?.focus();
            });
        } else {
            previousFocusRef.current?.focus();
        }
    }, [open]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!open) return;

            if (e.key === 'Escape') {
                onClose();
                return;
            }

            if (e.key === 'Tab' && panelRef.current) {
                const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS));
                if (focusable.length === 0) {
                    e.preventDefault();
                    return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    }
                } else if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        },
        [open, onClose],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!open) return null;

    const widthValue = typeof width === 'number' ? `${width}px` : width;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
    };

    return ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div className="bf-drawer-overlay" onClick={handleOverlayClick} role="presentation">
            <div
                ref={panelRef}
                className="bf-drawer-panel"
                style={{ width: widthValue }}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div className="bf-drawer-header">
                    {title && (
                        <span id={titleId} className="bf-drawer-title">
                            {title}
                        </span>
                    )}
                    <button type="button" className="bf-drawer-close-btn" onClick={onClose} aria-label="Close drawer">
                        ✕
                    </button>
                </div>
                <div className="bf-drawer-body">{children}</div>
            </div>
        </div>,
        document.body,
    );
}

export default BFDrawer;
